<script setup lang="ts">
// The ONE contact form shared by every site in the network. Explicit consent
// checkbox, honest copy, no pre-checked boxes. Posts the common contact
// schema to /api/contact; the backend tags the site from the Host header and
// composes a hidden summary from the attached context (decision-tree answers
// or an AI diagnosis) — nothing about that context is collected here beyond
// what the visitor already walked through.
//
// Two things beyond name-and-number: the service address is autocompleted
// (so a lead arrives with a real ZIP instead of "jax"), and an optional
// details block asks a handful of yes/no questions. Both are optional, and
// the form submits exactly as it always did when they're left alone.
import { computed, reactive, ref, watch } from 'vue'
import { NButton, NCheckbox, NInput } from 'naive-ui'
import { useMutation } from '@tanstack/vue-query'
import qualifierContract from '@contracts/lead-qualifiers.v1.json'
import { postJson } from '@/shared/api/client'
import AddressAutocomplete from '@/shared/components/AddressAutocomplete.vue'
import LeadQualifiers from '@/shared/components/LeadQualifiers.vue'
import { leadIntent } from '@/shared/state/leadIntent'
import type {
    AddressValue,
    ContactContext,
    ContactRequest,
    ContactResponse,
    LeadType,
    QualifierAnswers,
    QualifierDef,
} from '@/shared/api/types'

const props = defineProps<{
    heading?: string
    subheading?: string
    submitLabel?: string
    /** Structured evidence to attach (tree walk or diagnosis session). */
    context?: ContactContext
    descriptionPlaceholder?: string
}>()

const emit = defineEmits<{ submitted: [contactId: string] }>()

const QUALIFIERS = qualifierContract.qualifiers as unknown as QualifierDef[]

const form = reactive({
    name: '',
    email: '',
    phone: '',
    address: { formatted: '' } as AddressValue,
    address_line2: '',
    description: '',
    contact_ok: false,
})

const answers = ref<QualifierAnswers>({})
const detailsOpen = ref(false)
const submitted = ref(false)

// Which questions to ask: a visitor who hasn't stated an intent gets the
// repair set, which is what an unstated visitor almost always turns out to
// be — but their lead_type stays unsent so the backend can infer it from the
// evidence instead of us guessing here.
const askedQualifiers = computed(() =>
    QUALIFIERS.filter((q) => q.applies_to.includes(leadIntent.value ?? 'repair')),
)

// Arriving via a site's "I already need a replacement" button opens the
// details — those questions are the whole point of that button.
watch(
    leadIntent,
    (intent) => {
        if (intent === 'replace') detailsOpen.value = true
    },
    { immediate: true },
)

const replacing = computed(() => leadIntent.value === 'replace')

const heading = computed(
    () =>
        props.heading ??
        (replacing.value
            ? 'Ready to replace? Let’s get you real numbers.'
            : 'Want a professional to take a look?'),
)

const subheading = computed(
    () =>
        props.subheading ??
        (replacing.value
            ? 'You already know the system is coming out. Leave your details and a licensed local professional will reach out with replacement options and pricing — no obligation, no pressure.'
            : 'We can connect you with a licensed local HVAC professional to review your situation. Totally optional — no obligation, no pressure.'),
)

const submitLabel = computed(
    () =>
        props.submitLabel ??
        (replacing.value ? 'Get Replacement Quotes' : 'Request a Professional Opinion'),
)

const valid = computed(
    () =>
        form.name.trim().length > 0 &&
        (form.phone.trim().length > 0 || form.email.trim().length > 0) &&
        form.contact_ok,
)

/** Answered questions only — anything untouched stays out, and the backend
 * relays it as an explicit null. */
const answeredList = computed(() =>
    Object.entries(answers.value).map(([id, value]) => ({ id, value })),
)

const answeredCount = computed(() => answeredList.value.length)

/** Record one qualifier answer; `null` returns it to unanswered. Owning the
 * write here (rather than v-model'ing the map into the child) keeps rapid
 * answers from overwriting each other. */
function onAnswer(id: string, value: boolean | null) {
    const next = { ...answers.value }
    if (value === null) delete next[id]
    else next[id] = value
    answers.value = next
}

function setIntent(value: LeadType) {
    // Tapping the active choice returns to "hasn't said", which is a real
    // state: the backend then infers the type from the evidence.
    leadIntent.value = leadIntent.value === value ? null : value
}

const submit = useMutation({
    mutationFn: (body: ContactRequest) => postJson<ContactResponse>('/api/contact', body),
    onSuccess: (res) => {
        submitted.value = true
        emit('submitted', res.contact_id)
    },
})

function onSubmit() {
    if (!valid.value) return
    const address = form.address
    submit.mutate({
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: address.formatted.trim() || undefined,
        address_line2: form.address_line2.trim() || undefined,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        place_id: address.place_id,
        description: form.description.trim() || undefined,
        lead_type: leadIntent.value ?? undefined,
        qualifiers: answeredList.value.length ? answeredList.value : undefined,
        contact_ok: form.contact_ok,
        context: props.context,
    })
}
</script>

<template>
    <div class="contact-form">
        <template v-if="!submitted">
            <h2>{{ heading }}</h2>
            <p class="muted">{{ subheading }}</p>

            <div class="grid">
                <label>
                    <span>Name *</span>
                    <NInput v-model:value="form.name" placeholder="Your name" />
                </label>
                <label>
                    <span>Email</span>
                    <NInput v-model:value="form.email" inputmode="email" placeholder="you@example.com" />
                </label>
                <label>
                    <span>Phone</span>
                    <NInput v-model:value="form.phone" inputmode="tel" placeholder="(555) 555-0123" />
                </label>
                <label>
                    <span>Service address</span>
                    <AddressAutocomplete v-model="form.address" />
                </label>
                <label>
                    <span>Apt / Suite <em>(optional)</em></span>
                    <NInput v-model:value="form.address_line2" :maxlength="60" placeholder="Unit 4B" />
                </label>
                <label class="wide">
                    <span>What's going on?</span>
                    <NInput
                        v-model:value="form.description"
                        type="textarea"
                        :autosize="{ minRows: 3, maxRows: 8 }"
                        :maxlength="2000"
                        show-count
                        :placeholder="
                            descriptionPlaceholder ??
                            'Tell us about the system, the symptoms, or the quote you were given…'
                        "
                    />
                </label>
            </div>

            <section class="details">
                <button class="details-toggle" type="button" @click="detailsOpen = !detailsOpen">
                    <span class="chev" :class="{ open: detailsOpen }">›</span>
                    Optional details
                    <span class="muted small">
                        <template v-if="answeredCount">
                            — {{ answeredCount }} answered
                        </template>
                        <template v-else>
                            — helps the pro show up prepared
                        </template>
                    </span>
                </button>

                <div v-show="detailsOpen" class="details-body">
                    <div class="intent">
                        <span class="muted small">What do you need?</span>
                        <div class="intent-choices">
                            <button
                                type="button"
                                class="chip"
                                :class="{ picked: leadIntent === 'repair' }"
                                @click="setIntent('repair')"
                            >
                                A repair
                            </button>
                            <button
                                type="button"
                                class="chip"
                                :class="{ picked: leadIntent === 'replace' }"
                                @click="setIntent('replace')"
                            >
                                A replacement
                            </button>
                        </div>
                    </div>

                    <LeadQualifiers
                        :qualifiers="askedQualifiers"
                        :answers="answers"
                        @answer="onAnswer"
                    />
                    <p class="muted small">
                        Every one of these is optional — skip anything you'd rather not answer.
                    </p>
                </div>
            </section>

            <NCheckbox v-model:checked="form.contact_ok" class="consent">
                Yes — share my details with a licensed local HVAC professional so they can contact
                me about my system. That's the only thing your info is used for.
            </NCheckbox>

            <p class="muted small">Provide at least a phone number or an email.</p>

            <p class="region-notice">
                <strong>Service area: Northeast Florida.</strong> We work with licensed
                professionals in Jacksonville, St. Augustine, Ponte Vedra, Orange Park,
                Fernandina Beach and surrounding counties. Submissions from other regions are
                unsupported and may not receive a response.
            </p>

            <NButton
                type="primary"
                size="large"
                :loading="submit.isPending.value"
                :disabled="!valid"
                @click="onSubmit"
            >
                {{ submitLabel }}
            </NButton>
            <p v-if="submit.isError.value" class="error">
                {{ (submit.error.value as Error)?.message ?? 'Something went wrong — try again.' }}
            </p>
        </template>

        <div v-else class="thanks">
            <h2>Request received ✓</h2>
            <p class="muted">
                A licensed local professional will reach out soon. No spam, no pressure — and you
                can say no at any point.
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
.contact-form {
    .grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: var(--site-space-md);
        margin: var(--site-space-lg) 0;
    }

    label {
        display: flex;
        flex-direction: column;
        gap: var(--site-space-xs);

        span {
            font-size: 0.9rem;
            color: var(--site-text-muted);

            em {
                font-style: normal;
                opacity: 0.75;
            }
        }
    }

    .wide {
        grid-column: 1 / -1;
    }

    .consent {
        margin: var(--site-space-md) 0;
        max-width: 40rem;
    }

    .small {
        font-size: 0.85rem;
    }

    .details {
        margin-bottom: var(--site-space-lg);
        border: 1px solid var(--site-border);
        border-radius: var(--site-radius-md);
        padding: var(--site-space-sm) var(--site-space-md);
    }

    .details-toggle {
        display: flex;
        align-items: center;
        gap: var(--site-space-xs);
        width: 100%;
        background: none;
        border: none;
        padding: var(--site-space-xs) 0;
        cursor: pointer;
        color: var(--site-text);
        font-family: var(--site-font-body);
        font-size: 0.95rem;
        text-align: left;

        .chev {
            display: inline-block;
            transition: transform 0.15s ease;
            color: var(--site-accent);

            &.open {
                transform: rotate(90deg);
            }
        }
    }

    .details-body {
        padding-top: var(--site-space-sm);
    }

    .intent {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: var(--site-space-sm);
        padding-bottom: var(--site-space-sm);
    }

    .intent-choices {
        display: flex;
        gap: var(--site-space-xs);
    }

    .chip {
        padding: 0.35em 0.9em;
        border: 1px solid var(--site-border);
        border-radius: var(--site-radius-pill, 999px);
        background: none;
        color: var(--site-text-muted);
        font-family: var(--site-font-body);
        font-size: 0.88rem;
        cursor: pointer;

        &:hover {
            border-color: var(--site-accent);
            color: var(--site-accent);
        }

        &.picked {
            border-color: var(--site-accent);
            background: var(--site-accent-soft);
            color: var(--site-text);
        }
    }

    .region-notice {
        max-width: 40rem;
        font-size: 0.85rem;
        color: var(--site-text-muted);
        background: var(--site-bg-alt);
        border-left: 3px solid var(--site-accent);
        border-radius: 0 var(--site-radius-sm) var(--site-radius-sm) 0;
        padding: var(--site-space-sm) var(--site-space-md);
        margin: 0 0 var(--site-space-md);

        strong {
            color: var(--site-text);
        }
    }

    .error {
        color: var(--site-danger);
        margin-top: var(--site-space-sm);
    }

    .thanks {
        text-align: center;
        padding: var(--site-space-xl) 0;
    }
}
</style>
